import React from "react";
import {TableChartOutlined} from "@mui/icons-material";

import ReportResultTable from "../../../../components/Reporting/ReportResultTable";
import Applet from "../../Applet";

// A report's rows.
//
// COLUMNS ARE A FUNCTION OF THE LAST RUN, which is why Placement and Columns are built in render
// rather than sitting in state. State is set once at construction, before anybody has built a
// report -- a set of columns frozen then describes no report in particular. The same lesson
// LookupValueTableApplet records about its Parent column, and LookupDetailApplet about its View.
//
// THE HEADINGS COME FROM THE SERVER, beside the rows: it knows the field's label and whether the
// column was aggregated, so "Avg of Review Period" is composed where the specification is, not
// re-derived here from a spec the grid would have to be handed a second copy of.
class ReportResultTableApplet extends Applet {

    render() {
        const component = this.getEnterpriseComponent();

        // THE COLUMNS COME FROM THE PAGE, not from the component, and the difference is the whole
        // bug this replaces. They were read off the EC here -- `component.getResultColumns()` --
        // which is state that changes when the FETCH completes, and a change on an EC does not
        // re-render an applet. So the grid loaded its three rows, re-rendered them, and drew them
        // under a header that still said "No columns": rows present, columns empty, footer honestly
        // reporting "1 - 3 of 3". A React prop re-renders; a field on someone else's object does
        // not.
        const columns = (this.props.resultColumns ?? component?.getResultColumns?.() ?? []);

        // A LISTING'S ROWS ARE RECORDS, so each gets a way in. A summary row is a group; there is
        // nothing single to open, and the column is simply not there.
        //
        // FROM THE PAGE, FOR THE REASON WRITTEN ABOVE ABOUT THE COLUMNS. Read off the component
        // this is a fact that changes when the FETCH completes, and a change on an EC does not
        // re-render an applet -- so the column was decided from an empty key list and never
        // appeared. The same bug as the "No columns" header, in the same file, three lines apart.
        const openable = (this.props.recordKeys ?? component?.getRecordKeys?.() ?? [])
            .some(one => !!one);

        const placement = [
            ...columns.map((_column, position) => ({id: `c${position}`})),
            ...(openable ? [{id: "_open"}] : []),
        ];

        // WIDTHS ARE MEASURED, NOT GUESSED, as far as a heading can be: a column headed "Avg of
        // Review Period (months)" needs more room than one headed "Quality". Guessing a single
        // width has cost two rounds of correction on this app's other grids.
        const definitions = columns.map((column, position) => ({
            id: `c${position}`,
            width: Math.min(420, Math.max(140, String(column.Label ?? "").length * 9 + 60)),
            cellRenderer: "Standard Single Cell",
            header: "enabled",
            headerRenderer: "Standard Single Header",
            headerTitle: column.Label ?? `Column ${position + 1}`,
        }));

        if (openable) {
            definitions.push({
                id: "_open",
                width: 60,
                cellRenderer: "Open Record Cell",
                header: "enabled",
                headerRenderer: "Standard Single Header",
                // UNHEADED. There is no value under it to name -- the column is an action, and
                // "Open" above a row of icons is a word doing no work.
                headerTitle: "",
            });
        }

        return <ReportResultTable applet={this}
                                  pageSize={this.state.PageSize}
                                  placement={{"xl": placement}}
                                  columns={definitions}
                                  title="Result"
                                  icon={<TableChartOutlined sx={{color: "#1498D8"}}/>}
                                  enterpriseComponent={component}
                                  sort={this.state.Sort}
                                  rowHeight={this.state.RowHeight}
                                  simpleSearch={this.state.SimpleSearch}
                                  readOnly={true}
        />;
    }

    state = {
        Name: "ReportResultTableApplet",
        RowHeight: 48,
        PageSize: {"default": 25, "xs": 5, "sm": 5},
        // EMPTY, deliberately. Everything about a report's columns is decided in render from what
        // the last run returned; anything here would be a second answer that is wrong until the
        // first run and stale after every one after it.
        Columns: [],
        Sort: [],
        SimpleSearch: true,
    };
}

export default ReportResultTableApplet;
